import { createInstance, InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ICU from 'i18next-icu';
import resourcesToBackend from 'i18next-resources-to-backend';
import { getLanguageLocale } from './helpers/getLanguageLocale';
import { IConfig, NamespaceResourcesMap } from './types';

interface IInitTranslation<Lang extends string> {
  config: IConfig,
  locale: Lang,
  namespaces?: string[],
  resources: NamespaceResourcesMap<Lang>,
}

export const createI18nService = <Lang extends string>({
  config,
  locale,
  resources,
  namespaces = [],
}: IInitTranslation<Lang>) => {
  const i18nInstance = createInstance();

  i18nInstance.use(initReactI18next);

  const namespacesFromResources = Object.keys(resources?.[locale] || {});

  const mergedNamespaces = [...namespaces, ...namespacesFromResources];

  const i18nextOptions: InitOptions & { localeDetectionPaths?: string[] } = {
    resources,
    fallbackLng: config.defaultLocale,
    supportedLngs: config.locales,
    defaultNS: mergedNamespaces?.[0],
    fallbackNS: mergedNamespaces?.[0],
    ns: mergedNamespaces ? [...mergedNamespaces] : [],
    preload: resources ? [] : config.locales,
    interpolation: {
      escapeValue: false,
    },
    localeDetectionPaths: ['^/$'],
    react: {
      useSuspense: false,
      bindI18nStore: 'added',
    },
    detection: config.detection,
  };

  i18nInstance
    .use(resourcesToBackend(config.getResourcesFromBackend))
    .use(LanguageDetector)
    .use(ICU)
    .init(i18nextOptions);
  // *** call to use language detector
  // *** this is needed to detect language on server side
  i18nInstance.changeLanguage(getLanguageLocale(locale));

  return i18nInstance;
};
